import { useState, useEffect, useCallback, useRef } from 'react';
import { useFormData } from './useFormData';

/**
 * Custom hook for managing detail view state
 * Standardizes loading, editing, and saving patterns for entity detail pages
 *
 * @param {object} options - Configuration options
 * @param {Function} options.fetchData - Async function to fetch entity data (receives id)
 * @param {Function} options.saveData - Async function to save entity data (receives id, data)
 * @param {Function} options.deleteData - Optional async function to delete entity (receives id)
 * @param {Function} options.onSaveSuccess - Optional callback after successful save
 * @param {Function} options.onDeleteSuccess - Optional callback after successful delete
 * @param {object} options.typeMap - Type map for form fields
 * @returns {object} Detail view state and controls
 *
 * @example
 * const {
 *   data,
 *   loading,
 *   isEditing,
 *   formData,
 *   error,
 *   successMessage,
 *   startEditing,
 *   cancelEditing,
 *   handleChange,
 *   handleSave,
 *   handleDelete,
 *   reload
 * } = useDetailView({
 *   fetchData: (id) => apiCall(`${API_BASE_URL}/customers/${id}`),
 *   saveData: (id, data) => apiCall(`${API_BASE_URL}/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
 *   onSaveSuccess: () => navigate('/customers')
 * });
 */
export const useDetailView = (options = {}) => {
  const {
    fetchData,
    saveData,
    deleteData,
    onSaveSuccess,
    onDeleteSuccess,
    typeMap = {},
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use refs to store functions to avoid infinite loops
  // This allows us to always use the latest functions without adding them to dependencies
  const fetchDataRef = useRef(fetchData);
  const saveDataRef = useRef(saveData);
  const deleteDataRef = useRef(deleteData);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const onDeleteSuccessRef = useRef(onDeleteSuccess);

  useEffect(() => {
    fetchDataRef.current = fetchData;
    saveDataRef.current = saveData;
    deleteDataRef.current = deleteData;
    onSaveSuccessRef.current = onSaveSuccess;
    onDeleteSuccessRef.current = onDeleteSuccess;
  }, [fetchData, saveData, deleteData, onSaveSuccess, onDeleteSuccess]);

  const {
    formData,
    setFormData,
    handleChange,
    resetFormTo,
    isDirty,
  } = useFormData({}, { typeMap });

  /**
   * Load entity data
   */
  const loadData = useCallback(async (id) => {
    if (!fetchDataRef.current || !id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchDataRef.current(id);
      const entityData = response?.data || response;

      if (entityData) {
        setData(entityData);
        resetFormTo(entityData);
      } else {
        setError('Data not found');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [resetFormTo]);

  /**
   * Reload data (useful after external changes)
   */
  const reload = useCallback(() => {
    if (data?.id) {
      loadData(data.id);
    }
  }, [data?.id, loadData]);

  /**
   * Start editing mode
   */
  const startEditing = useCallback(() => {
    setIsEditing(true);
    setError(null);
    setSuccessMessage(null);
  }, []);

  /**
   * Cancel editing and reset form
   */
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError(null);
    setSuccessMessage(null);
    if (data) {
      resetFormTo(data);
    }
  }, [data, resetFormTo]);

  /**
   * Save changes
   */
  const handleSave = useCallback(async (e, additionalValidation) => {
    if (e) {
      e.preventDefault();
    }

    if (!saveDataRef.current || !data?.id) return;

    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      // Run additional validation if provided
      if (additionalValidation) {
        const validationError = await additionalValidation(formData);
        if (validationError) {
          setError(validationError);
          setIsSaving(false);
          return;
        }
      }

      const response = await saveDataRef.current(data.id, formData);

      if (response?.success !== false) {
        const updatedData = response?.data || response;
        setData(updatedData);
        resetFormTo(updatedData);
        setSuccessMessage('Changes saved successfully');
        setIsEditing(false);

        if (onSaveSuccessRef.current) {
          onSaveSuccessRef.current(updatedData);
        }
      } else {
        setError(response?.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving data:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [data?.id, formData, resetFormTo]);

  /**
   * Delete entity
   */
  const handleDelete = useCallback(async () => {
    if (!deleteDataRef.current || !data?.id) return;

    setError(null);
    setIsDeleting(true);

    try {
      await deleteDataRef.current(data.id);
      setSuccessMessage('Deleted successfully');

      if (onDeleteSuccessRef.current) {
        onDeleteSuccessRef.current();
      }
    } catch (err) {
      console.error('Error deleting data:', err);
      setError(err.message || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  }, [data?.id]);

  /**
   * Clear messages
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  return {
    // Data state
    data,
    setData,
    loading,
    isEditing,
    error,
    successMessage,
    isSaving,
    isDeleting,

    // Form state
    formData,
    setFormData,
    handleChange,
    isDirty,

    // Actions
    loadData,
    reload,
    startEditing,
    cancelEditing,
    handleSave,
    handleDelete,
    clearMessages,
  };
};
